import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ChatFilesPicker } from '../components/chat/ChatFilesPicker';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { StatusBadge } from '../components/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ChevronLeft, Save, Loader2, Plus, X, Upload, Paperclip, FileText, Link2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { FormattedInput } from '../components/FormattedInput';
import { DocumentUpload } from '../components/DocumentUpload';
import { validateNIF, validatePhone } from '../utils/ptValidators';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const COMPANY_NAMES = {
  edp: 'EDP',
  galp: 'Galp',
  endesa: 'Endesa',
  iberdrola: 'Iberdrola',
  gold: 'Gold Energy',
  repsol: 'Repsol',
  yes: 'Yes',
  meo_energia: 'Meo Energia',
  nos: 'NOS',
  meo: 'MEO',
  vodafone: 'Vodafone',
  nos_securitas: 'NOS Securitas',
  prosegur: 'Prosegur'
};

const CAMPAIGNS_FALLBACK = {
  endesa: ['Quero +', '+ Energia', 'Stores', 'Aniversário', 'Happy - SDD + FE', 'Tarifa Sua Empresa - 8%', 'Tarifa Sua Empresa - 10%', 'Tarifa Sua Empresa - 12%', 'Tarifa Sua Empresa - 14%'],
  galp: ['Casa & Estrada', 'GPL Canalizado', 'Galp Negócios', 'Galp Negócios TH', 'Plano Segunda Casa', 'Plano Tri-Horário'],
  repsol: ['Plano Leve', 'Plano Leve Pro Sem Mais', 'Plano Leve Sem Mais', 'Plano Viva', 'Plano Viva Sem Mais', 'Plano Viva Pro Sem Mais', 'Leve Pro Sem Mais Silver', 'Leve Pro Sem Mais Gold'],
  edp: ['Tarifa Luz', 'Tarifa Gás', 'Tarifa Luz e Gás'],
  iberdrola: ['Mcasa Luz', 'Mcasa Luz B', 'Mcasa Gas', 'Mcasa Gás Dual', 'Mneg Luz', 'Mneg Gás', 'Mneg Index'],
  gold: ['Tarifa Luz', 'Tarifa Gás', 'Tarifa Luz e Gás'],
  yes: ['Tarifa Luz'],
};

const SOLAR_PANELS = {
  galp: ['Special - 380Wp', 'Premium - 355Wp', 'Start - 415Wp', 'Advanced - 405Wp', 'Painéis 485Wp'],
  edp: ['Gama quality - 420Wp', 'Gama premium - 450Wp'],
  iberdrola: ['Smart Solar - 450Wp'],
};

const SOLAR_PAYMENT = {
  galp: ['Pronto Pagamento', '48 Meses', '60 Meses'],
  edp: ['Pronto Pagamento', '24 Meses', '36 Meses', '60 Meses', '96 Meses'],
  iberdrola: ['Pronto Pagamento', '36 Meses'],
};

const SOLAR_STATUSES = [
  { value: 'pedido_orcamento', label: 'Pedido de Orçamento' },
  { value: 'orcamento_enviado', label: 'Orçamento Enviado' },
  { value: 'nao_atende', label: 'Não Atende' },
  { value: 'contrato_assinado', label: 'Contrato Assinado' },
  { value: 'instalacao_agendada', label: 'Instalação Agendada' },
  { value: 'instalacao_concluida', label: 'Instalação Concluída' },
  { value: 'ativacao', label: 'Ativação' },
  { value: 'anulado', label: 'Anulado' },
  { value: 'baixa', label: 'Baixa' },
  { value: 'concluido', label: 'Concluído' },
];

const CHARGER_PRODUCTS = {
  edp: ['Premium Charger EDP', 'Standard Charger EDP', 'Light Charger EDP', 'Plug in EDP'],
  iberdrola: ['Tomada Reforçada', 'Wallbox Pulsar Max', 'Wallbox Pulsar Pro'],
  galp: ['Wallbox Pulsar Max', 'Wallbox Pulsar Pro', 'Circutor eNext'],
};

const CHARGER_POWER = ['3,7kW', '7,4kW', '11kW', '22kW'];

const STATUSES = [
  { value: 'pedido_chamada', label: 'Pedido de Chamada' },
  { value: 'validacao_documentos', label: 'Validação de Documentos' },
  { value: 'chamada_efectuada', label: 'Chamada Efectuada' },
  { value: 'nao_atende', label: 'Não Atende' },
  { value: 'esclarecimentos', label: 'Esclarecimentos' },
  { value: 'pendente_documentos', label: 'Pendente de Documentos' },
  { value: 'anulado_comercializadora', label: 'Anulado pela Comercializadora' },
  { value: 'registado_comercializadora', label: 'Registado na Comercializadora' },
  { value: 'anulado', label: 'Anulado' },
  { value: 'baixa', label: 'Baixa' },
  { value: 'concluido', label: 'Concluído' },
];

export const ContractForm = () => {
  const { category, company, id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isManager, isSuperAdmin, hasPerm } = useAuth();
  const isEditing = Boolean(id) && id !== 'new';

  // Return URL — where to go after save/cancel. Set by callers via location.state.returnTo
  // or query string ?return_to=. Falls back to the company contract list.
  const returnTo = location.state?.returnTo || new URLSearchParams(location.search).get('return_to') || null;

  const goBack = () => {
    if (returnTo) {
      navigate(returnTo);
    } else if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(`/contracts/${category}/${company}`);
    }
  };
  
  // Edit access: Super Admin + Gestor + Admin can edit; others go to detail.
  // Backend already enforces deeper permissions per role (server.py update_contract).
  useEffect(() => {
    const canEdit = isSuperAdmin() || user?.role === 'gestor' || user?.role === 'admin';
    if (isEditing && !canEdit) {
      navigate(`/contracts/${category}/${company}/${id}`, { replace: true });
    }
  }, [isEditing, navigate, category, company, id, isSuperAdmin, user?.role]);
  
  // Block contract creation if partner doesn't have permission
  useEffect(() => {
    if (!isEditing && !hasPerm('criar_contratos')) {
      navigate('/contracts/all', { replace: true });
    }
  }, [isEditing, hasPerm, navigate]);
  
  const companyName = COMPANY_NAMES[company] || company?.toUpperCase();
  const isSolar = category === 'solar';
  const isTelecom = category === 'telecomunicacoes';
  const isCarregamento = category === 'carregamento';
  const isAlarme = category === 'alarmes';
  const isEnergy = !isSolar && !isTelecom && !isCarregamento && !isAlarme;
  const [campaigns, setCampaigns] = useState(CAMPAIGNS_FALLBACK[company] || []);
  const solarPanels = SOLAR_PANELS[company] || [];
  const solarPayments = SOLAR_PAYMENT[company] || [];
  const chargerProducts = CHARGER_PRODUCTS[company] || [];
  const statusOptions = isSolar ? SOLAR_STATUSES : STATUSES;
  
  // Load campaigns from backend (overrides + defaults). Falls back to hardcoded list.
  useEffect(() => {
    if (!category || !company) return;
    let cancelled = false;
    axios.get(`${API_URL}/config/companies/${category}/${company}/campaigns`)
      .then(res => {
        if (cancelled) return;
        const list = res.data?.campaigns || [];
        setCampaigns(list.length > 0 ? list : (CAMPAIGNS_FALLBACK[company] || []));
      })
      .catch(() => {
        if (!cancelled) setCampaigns(CAMPAIGNS_FALLBACK[company] || []);
      });
    return () => { cancelled = true; };
  }, [category, company]);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Duplicate detection modal (NIF + CPE/CUI within 30 days, excludes anulado)
  const [duplicateModal, setDuplicateModal] = useState(null);
  const [nifConflictModal, setNifConflictModal] = useState(null);
  const [nifComprovativoChecked, setNifComprovativoChecked] = useState(false);
  // Proactive duplicate warning shown inline as the user fills NIF + CPE/CUI
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  // Aviso imediato: NIF já existe no sistema (e se pertence a cliente com nome diferente)
  const [nifNameWarning, setNifNameWarning] = useState(null);
  // Fluxo de confirmação de comprovativo (2 perguntas) e bloqueio de continuação
  const [nifCheckStep, setNifCheckStep] = useState(null); // null | 'step1' | 'step2' | 'step3'
  const [nifBlocked, setNifBlocked] = useState(false);
  const [nifProofFile, setNifProofFile] = useState(null);
  const promptedNifRef = useRef(null);
  const alertedNifRef = useRef(null);
  const [duplicateChecking, setDuplicateChecking] = useState(false);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [partners, setPartners] = useState([]);
  const chatFilesRef = useRef(null);
  const documentUploadRef = useRef(null);

  // Companies available for "Comercializadora Anterior" dropdown (filtered by category).
  // Uses a local static fallback so the dropdown never shows empty in case the
  // /config/companies endpoint fails (CORS, network, auth race, etc.).
  // The URL category may be an alias `energias` or `luz` — backend & static map use `luz_gas`.
  const categoryKey = (category === 'energias' || category === 'luz') ? 'luz_gas' : category;
  const COMPANIES_BY_CATEGORY = {
    luz_gas: [
      { slug: 'edp', name: 'EDP' },
      { slug: 'galp', name: 'Galp' },
      { slug: 'endesa', name: 'Endesa' },
      { slug: 'iberdrola', name: 'Iberdrola' },
      { slug: 'gold', name: 'Gold Energy' },
      { slug: 'repsol', name: 'Repsol' },
      { slug: 'yes', name: 'Yes' },
      { slug: 'meo_energia', name: 'Meo Energia' },
    ],
    solar: [
      { slug: 'edp', name: 'EDP' },
      { slug: 'galp', name: 'Galp' },
      { slug: 'iberdrola', name: 'Iberdrola' },
    ],
    carregamento: [
      { slug: 'edp', name: 'EDP' },
      { slug: 'galp', name: 'Galp' },
      { slug: 'iberdrola', name: 'Iberdrola' },
    ],
    telecomunicacoes: [
      { slug: 'nos', name: 'NOS' },
      { slug: 'meo', name: 'MEO' },
      { slug: 'vodafone', name: 'Vodafone' },
    ],
    alarmes: [
      { slug: 'nos_securitas', name: 'NOS Securitas' },
      { slug: 'prosegur', name: 'Prosegur' },
    ],
  };
  const [prevCompanies, setPrevCompanies] = useState(COMPANIES_BY_CATEGORY[categoryKey] || []);
  useEffect(() => {
    if (!categoryKey) { setPrevCompanies([]); return; }
    // Start with static fallback so the dropdown always has options
    setPrevCompanies(COMPANIES_BY_CATEGORY[categoryKey] || []);
    let cancelled = false;
    axios.get(`${API_URL}/config/companies/${categoryKey}`)
      .then(res => {
        if (cancelled) return;
        const map = res.data || {};
        const list = Object.entries(map).map(([slug, info]) => ({ slug, name: info?.name || slug }));
        if (list.length > 0) setPrevCompanies(list);
      })
      .catch(() => { /* silent — keep static fallback */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryKey]);

  // Iberdrola eligibility check state (effect declared after formData below)
  const [iberdrolaCheck, setIberdrolaCheck] = useState(null);
  const [iberdrolaChecking, setIberdrolaChecking] = useState(false);

  const [formData, setFormData] = useState({
    service_category: category,
    company: company,
    // Tipo de Segmento / Autorizações
    tipo_segmento: '',
    tipo_produto: '',
    contratacao: '',
    localidade: '',
    indoor: '',
    controlo_qualidade: '',
    cpe: '',
    cui: '',
    // Checkboxes
    luz_mc: false,
    luz_at: false,
    luz_ed: false,
    gas_mc: false,
    gas_at: false,
    gas_ed: false,
    cartao_cidadao: '',
    // Dados Cliente
    nome_cliente: '',
    nif: '',
    nif_comprovativo: false,
    comercializadora_anterior: '',
    telefones: [{ tipo: 'telemovel', numero: '' }],
    cae: '',
    crc: '',
    // Dados Produto
    campanha: '',
    sva: '',
    // Solar-specific
    contratacao_energia: '',
    tipo_painel: '',
    microinversor: '',
    baterias: '',
    numero_paineis_solares: '',
    metodo_pagamento: '',
    // Telecom-specific
    portabilidade: '',
    pacote_adesao: '',
    ofertas_telecom: '',
    valor_pacote: '',
    // Carregamento-specific
    potencia_carregador: '',
    produto_carregador: '',
    // Energia — potência contratada (kVA)
    potencia: '',
    escalao_gas: '',
    // Alarmes-specific
    tipo_alarme: '',
    kit_alarme: '',
    n_divisoes: '',
    periodo_fidelizacao: '',
    valor_mensal_alarme: '',
    // Dados Faturação
    fatura_eletronica: '',
    emails: [''],
    debito_direto: '',
    iban: '',
    // Observações
    tipo_pendencia: '',
    // Morada de Fornecimento / Instalação
    morada_fornecimento: '',
    nr_porta_fornecimento: '',
    andar_fornecimento: '',
    lado_fornecimento: '',
    codigo_postal_fornecimento: '',
    localidade_fornecimento: '',
    morada_faturacao_igual: '',
    // Morada de Faturação
    morada_faturacao: '',
    nr_porta_faturacao: '',
    andar_faturacao: '',
    lado_faturacao: '',
    codigo_postal_faturacao: '',
    localidade_faturacao: '',
    // Estado
    status: '',
    data_assinatura: '',
    agendamento: '',
    // Solar dates
    data_contrato: '',
    data_registo: '',
    data_instalacao: '',
    data_ativacao: '',
    data_baixa: '',
    nr_lead: '',
    offer: '',
    // Financial
    valor_comissao: '',
    estado_pagamento: 'pendente',
    receita_empresa: '',
    comissao_parceiro: '',
    estado_pagamento: 'pendente',
    // Assignment
    assigned_to: '',
    partner_id: '',
    team_id: '',
    // Lead link
    lead_id: '',
  });

  // Iberdrola eligibility check (when user is contracting Iberdrola and types NIF)
  useEffect(() => {
    if (company !== 'iberdrola') { setIberdrolaCheck(null); return; }
    const nif = (formData.nif || '').trim();
    if (nif.length < 9) { setIberdrolaCheck(null); return; }
    let cancelled = false;
    setIberdrolaChecking(true);
    const t = setTimeout(() => {
      axios.get(`${API_URL}/tools/check-iberdrola/${nif}`)
        .then(res => { if (!cancelled) setIberdrolaCheck(res.data); })
        .catch(() => { if (!cancelled) setIberdrolaCheck(null); })
        .finally(() => { if (!cancelled) setIberdrolaChecking(false); });
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [company, formData.nif]);

  // Proactive duplicate check — runs on create only, debounced.
  // Triggers when NIF + (CPE or CUI) are present and the NIF is at least 9 chars.
  useEffect(() => {
    if (isEditing) { setDuplicateWarning(null); return; }
    const nif = (formData.nif || '').trim();
    const cpe = (formData.cpe || '').trim();
    const cui = (formData.cui || '').trim();
    if (nif.length < 9 || (!cpe && !cui)) {
      setDuplicateWarning(null);
      return;
    }
    let cancelled = false;
    setDuplicateChecking(true);
    const t = setTimeout(() => {
      axios.get(`${API_URL}/contracts/check-duplicate`, {
        params: {
          nif,
          cpe: cpe || undefined,
          cui: cui || undefined,
          data_assinatura: formData.data_assinatura || undefined,
        }
      })
        .then(res => { if (!cancelled) setDuplicateWarning(res.data?.duplicate ? res.data : null); })
        .catch(() => { if (!cancelled) setDuplicateWarning(null); })
        .finally(() => { if (!cancelled) setDuplicateChecking(false); });
    }, 600);
    return () => { cancelled = true; clearTimeout(t); };
  }, [isEditing, formData.nif, formData.cpe, formData.cui, formData.data_assinatura]);

  // Aviso imediato de NIF — assim que o NIF (9 dígitos) é introduzido, verifica se já existe.
  useEffect(() => {
    if (isEditing) { setNifNameWarning(null); return; }
    const nif = (formData.nif || '').trim();
    if (nif.length < 9 || !validateNIF(nif).ok) {
      setNifNameWarning(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      axios.get(`${API_URL}/contracts/check-nif`, {
        params: { nif, nome_cliente: formData.nome_cliente || undefined },
      })
        .then(res => {
          if (cancelled) return;
          const data = res.data?.exists ? res.data : null;
          setNifNameWarning(data);
          if (data && data.conflict) {
            // Conflito (nome diferente) — abrir logo o fluxo de confirmação (uma vez por NIF)
            if (promptedNifRef.current !== nif && !formData.nif_comprovativo) {
              promptedNifRef.current = nif;
              setNifCheckStep('step1');
            }
          } else {
            // Sem conflito — limpar bloqueio e comprovativo
            promptedNifRef.current = null;
            setNifBlocked(false);
            setNifProofFile(null);
            if (formData.nif_comprovativo) {
              setFormData(prev => ({ ...prev, nif_comprovativo: false }));
            }
          }
        })
        .catch(() => { if (!cancelled) setNifNameWarning(null); });
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [isEditing, formData.nif, formData.nome_cliente]);



  useEffect(() => {
    fetchInitialData();
    if (isEditing) {
      fetchContract();
    } else {
      // Auto-assign to current user if comercial
      if (user?.role === 'comercial') {
        setFormData(prev => ({ ...prev, assigned_to: user.id }));
      }
      // Check for lead prefill (coming from /leads/:id "Converter em Contrato")
      try {
        const stored = sessionStorage.getItem('lead_prefill');
        if (stored) {
          const pre = JSON.parse(stored);
          sessionStorage.removeItem('lead_prefill');
          setFormData(prev => ({
            ...prev,
            nome_cliente: pre.nome_cliente || prev.nome_cliente,
            nif: pre.nif || prev.nif,
            morada_fornecimento: pre.morada_fornecimento || prev.morada_fornecimento,
            localidade_fornecimento: pre.localidade_fornecimento || prev.localidade_fornecimento,
            codigo_postal_fornecimento: pre.codigo_postal || prev.codigo_postal_fornecimento,
            cpe: pre.cpe || prev.cpe,
            cui: pre.cui || prev.cui,
            observacoes: pre.observacoes || prev.observacoes,
            telefones: (pre.telefones && pre.telefones.length > 0 && pre.telefones[0])
              ? pre.telefones.map(n => ({ tipo: 'telemovel', numero: n }))
              : prev.telefones,
            emails: (pre.emails && pre.emails.length > 0 && pre.emails[0])
              ? pre.emails
              : prev.emails,
            lead_id: pre.lead_id || prev.lead_id,
          }));
        }
      } catch { /* ignore */ }
      // Check for duplicate data in URL params — keep ONLY: nome, NIF, telefones, IBAN, emails
      const params = new URLSearchParams(window.location.search);
      if (params.get('duplicate') === 'true') {
        const telefonesStr = params.get('telefones');
        let telefones = [{ tipo: 'telemovel', numero: '' }];
        try {
          if (telefonesStr) telefones = JSON.parse(telefonesStr);
        } catch (e) { /* ignore */ }
        const emailsStr = params.get('emails');
        let emails = [''];
        try {
          if (emailsStr) emails = JSON.parse(emailsStr);
        } catch (e) { /* ignore */ }
        setFormData(prev => ({
          ...prev,
          nome_cliente: params.get('nome_cliente') || '',
          nif: params.get('nif') || '',
          iban: params.get('iban') || '',
          telefones: telefones.length > 0 ? telefones : [{ tipo: 'telemovel', numero: '' }],
          emails: emails.length > 0 ? emails : [''],
        }));
      }
      
      // Pre-fill from lead conversion
      if (params.get('from_lead') === 'true') {
        const leadPhone = params.get('cliente_telefone');
        const leadEmail = params.get('cliente_email');
        // UPPERCASE para consistência com o resto do contrato (corrige leads antigas em mixed case)
        const up = (s) => (s || '').toUpperCase();
        setFormData(prev => ({
          ...prev,
          nome_cliente: up(params.get('cliente_nome')),
          nif: params.get('cliente_nif') || '',
          telefones: leadPhone ? [{ tipo: 'telemovel', numero: leadPhone }] : [{ tipo: 'telemovel', numero: '' }],
          emails: leadEmail ? [leadEmail] : [''],
          morada_fornecimento: up(params.get('cliente_morada')),
          localidade_fornecimento: up(params.get('cliente_localidade')),
          localidade: up(params.get('cliente_localidade')),
          lead_id: params.get('lead_id') || '',
          comercializadora_anterior: params.get('comercializadora_anterior') || prev.comercializadora_anterior || '',
        }));
      }
    }
  }, [id]);

  // 2026-05-20: Sincronizar Localidade do topo (Tipo Segmento) com a Localidade da Morada de Fornecimento.
  // A do topo é read-only e reflete sempre a da Morada de Fornecimento (fonte de verdade).
  useEffect(() => {
    setFormData((prev) => {
      if (prev.localidade === prev.localidade_fornecimento) return prev;
      return { ...prev, localidade: prev.localidade_fornecimento || '' };
    });
  }, [formData.localidade_fornecimento]);

  const fetchInitialData = async () => {
    try {
      const [usersRes, teamsRes, partnersRes] = await Promise.all([
        axios.get(`${API_URL}/users`),
        axios.get(`${API_URL}/teams`),
        axios.get(`${API_URL}/partners`)
      ]);
      setUsers(usersRes.data);
      setTeams(teamsRes.data);
      setPartners(partnersRes.data);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    }
  };

  const fetchContract = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/contracts/${id}`);
      const contract = response.data;
      // Normalize comercializadora_anterior to lowercase slug (legacy data may have UPPERCASE due to handleChange bug)
      const ca = contract.comercializadora_anterior;
      if (typeof ca === 'string' && ca !== ca.toLowerCase()) {
        contract.comercializadora_anterior = ca.toLowerCase();
      }
      setFormData({
        ...contract,
        telefones: contract.telefones?.length > 0 ? contract.telefones : [{ tipo: 'telemovel', numero: '' }],
        emails: contract.emails?.length > 0 ? contract.emails : [''],
      });
    } catch (error) {
      toast.error('Erro ao carregar contrato');
      navigate(`/contracts/${category}/${company}`);
    } finally {
      setLoading(false);
    }
  };

  // Roles that must confirm attribution before saving (they create contracts
  // for other people and historically forgot to set Proprietário/Parceiro)
  const isAttributionGatedRole = ['super_admin', 'gestor', 'admin'].includes(user?.role);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const validateBeforeSubmit = () => {
    if (nifBlocked) {
      toast.error('Não é possível continuar: sem comprovativo do NIF divergente.');
      return false;
    }
    if (!formData.nome_cliente.trim()) {
      toast.error('Nome do cliente é obrigatório');
      return false;
    }
    const nifCheck = validateNIF(formData.nif);
    if (!nifCheck.ok) {
      toast.error(`NIF: ${nifCheck.error}`);
      return false;
    }
    for (let i = 0; i < formData.telefones.length; i++) {
      const t = formData.telefones[i];
      if (!t.numero) continue;
      const pc = validatePhone(t.numero, t.tipo);
      if (!pc.ok) {
        toast.error(`Telefone #${i + 1} (${t.tipo}): ${pc.error}`);
        return false;
      }
    }
    // Energia: potência contratada obrigatória quando há eletricidade (luz ou luz+gás)
    if (isEnergy && (formData.tipo_produto === 'luz' || formData.tipo_produto === 'luz_gas')) {
      if (!formData.potencia) {
        toast.error('Potência contratada é obrigatória (Energia).');
        return false;
      }
    }
    if (isEnergy && !isEditing && (formData.tipo_produto === 'gas' || formData.tipo_produto === 'luz_gas') && !formData.escalao_gas) {
      toast.error('Escalão de gás é obrigatório (Energia).');
      return false;
    }
    // Hard-required for Admin/Gestor/Super Admin (they create on behalf of others)
    if (isAttributionGatedRole) {
      if (!formData.assigned_to) {
        toast.error('Proprietário (Comercial) é obrigatório para Admin/Gestor/Super Admin');
        return false;
      }
      const pid = formData.partner_id === 'none' ? '' : formData.partner_id;
      if (!pid) {
        toast.error('Parceiro é obrigatório para Admin/Gestor/Super Admin');
        return false;
      }
    }
    return true;
  };

  const performSave = async ({ force = false, nifComprovativo = false } = {}) => {
    setSaving(true);
    try {
      const data = {
        ...formData,
        nif_comprovativo: nifComprovativo || formData.nif_comprovativo || false,
        telefones: formData.telefones.filter(t => t.numero),
        emails: formData.emails.filter(e => e),
        numero_paineis_solares: formData.numero_paineis_solares === '' ? null : formData.numero_paineis_solares,
        valor_comissao: formData.valor_comissao === '' ? null : parseFloat(formData.valor_comissao),
        receita_empresa: formData.receita_empresa === '' ? null : parseFloat(formData.receita_empresa),
        comissao_parceiro: formData.comissao_parceiro === '' ? null : parseFloat(formData.comissao_parceiro),
        partner_id: formData.partner_id === 'none' ? '' : formData.partner_id,
      };

      if (isEditing) {
        await axios.put(`${API_URL}/contracts/${id}`, data);
        toast.success('Contrato atualizado');
      } else {
        const url = force ? `${API_URL}/contracts?force=true` : `${API_URL}/contracts`;
        const response = await axios.post(url, data);
        const newContractId = response.data.id;
        if (documentUploadRef.current?.hasPendingFiles()) {
          try {
            await documentUploadRef.current.uploadPendingFiles(newContractId);
          } catch (uploadErr) {
            console.error('File upload error:', uploadErr);
            toast.error('Contrato criado mas erro ao carregar ficheiros');
          }
        }
        if (chatFilesRef.current?.hasSelected()) {
          try { await chatFilesRef.current.attachTo(newContractId); } catch { toast.error('Contrato criado, mas falhou juntar os ficheiros do chat'); }
        }
        if (nifProofFile) {
          try {
            const fd = new FormData();
            fd.append('file', nifProofFile);
            fd.append('contract_id', newContractId);
            await axios.post(`${API_URL}/files/upload`, fd);
          } catch (proofErr) {
            console.error('NIF proof upload error:', proofErr);
            toast.error('Contrato criado, mas falhou o upload do comprovativo do NIF.');
          }
        }
        toast.success(force ? 'Contrato criado (duplicado forçado)' : 'Contrato criado');
        const warnings = response.data?.duplicate_warnings || [];
        if (data.nif_comprovativo) {
          toast.warning('Contrato marcado como DIVERGENTE (NIF). Alerta urgente enviado ao Gestor/Super Admin.', { duration: 8000 });
        }
        if (warnings.length > 0) {
          const txt = warnings.map(w => `${w.field} (#${w.existing?.codigo})`).join(', ');
          toast.warning(`Atenção: dados de contacto repetidos — ${txt}. Gestor/Super Admin notificados.`, { duration: 8000 });
        }
      }
      setConfirmOpen(false);
      setDuplicateModal(null);
      setNifConflictModal(null);
      goBack();
    } catch (error) {
      const detail = error.response?.data?.detail;
      // NIF pertence a outra pessoa (nome diferente) — bloqueio com opção de comprovativo
      if (error.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'NIF_NAME_CONFLICT') {
        setNifComprovativoChecked(false);
        setNifConflictModal(detail);
        setConfirmOpen(false);
        return;
      }
      // Duplicate-contract conflict (409 with structured detail)
      if (error.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'DUPLICATE_CONTRACT') {
        setDuplicateModal(detail);
        setConfirmOpen(false);
        return;
      }
      let message;
      if (Array.isArray(detail)) {
        message = detail.map(d => typeof d === 'object' ? d.msg : d).join(', ');
      } else if (typeof detail === 'string') {
        message = detail;
      } else if (typeof detail === 'object') {
        message = JSON.stringify(detail);
      } else {
        message = isEditing ? 'Erro ao atualizar contrato' : 'Erro ao criar contrato';
      }
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const sendNifDivergenceAlert = React.useCallback(() => {
    const nif = (formData.nif || '').trim();
    if (!nif || alertedNifRef.current === nif) return;
    alertedNifRef.current = nif;
    axios.post(`${API_URL}/contracts/nif-divergence-alert`, {
      nif,
      nome_cliente: formData.nome_cliente || '',
      existing: nifNameWarning?.existing || {},
    }).catch(() => {});
  }, [formData.nif, formData.nome_cliente, nifNameWarning]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateBeforeSubmit()) return;
    if (isAttributionGatedRole) {
      setConfirmOpen(true);
      return;
    }
    await performSave();
  };

  // Fields that should NOT be auto-uppercased (selects, checkboxes, free-text)
  const SKIP_UPPERCASE = new Set([
    'tipo_segmento', 'tipo_produto', 'contratacao', 'indoor', 'sva',
    'fatura_eletronica', 'debito_direto', 'cartao_cidadao', 'campanha',
    'contratacao_energia', 'tipo_painel', 'microinversor', 'baterias',
    'metodo_pagamento', 'portabilidade', 'pacote_adesao', 'ofertas_telecom',
    'valor_pacote', 'potencia_carregador', 'produto_carregador', 'potencia', 'escalao_gas',
    'tipo_alarme', 'kit_alarme', 'n_divisoes', 'periodo_fidelizacao',
    'morada_faturacao_igual', 'tipo_pendencia', 'status',
    'estado_pagamento', 'assigned_to', 'partner_id', 'team_id', 'lead_id',
    // Booleans & numeric
    'luz_mc', 'luz_at', 'luz_ed', 'gas_mc', 'gas_at', 'gas_ed',
    'valor_comissao', 'receita_empresa', 'comissao_parceiro', 'comissao_angariador',
    'bonus_chefe', 'valor_mensal_alarme', 'numero_paineis_solares',
    // Dates
    'data_assinatura', 'agendamento', 'data_contrato', 'data_registo',
    'data_instalacao', 'data_ativacao', 'data_baixa',
    // Postal codes (formatted separately)
    'codigo_postal_fornecimento', 'codigo_postal_faturacao',
    // Slug-based selects (must keep lowercase to match SelectItem values)
    'comercializadora_anterior',
  ]);

  const handleChange = (field, value) => {
    let v = value;
    if (typeof v === 'string' && !SKIP_UPPERCASE.has(field)) {
      v = v.toUpperCase();
    }
    setFormData(prev => ({ ...prev, [field]: v }));
  };

  // Postal code handler: auto-format 7 digits to XXXX-XXX and lookup address
  // - Cache em sessionStorage para evitar chamadas repetidas à API
  // - Backoff em caso de HTTP 429 (rate limiting)
  const handlePostalCode = async (field, raw, moradaField, localidadeField) => {
    // Strip non-digits
    const digits = raw.replace(/\D/g, '').slice(0, 7);
    // Format with hyphen
    let formatted = digits;
    if (digits.length > 4) {
      formatted = digits.slice(0, 4) + '-' + digits.slice(4);
    }
    setFormData(prev => ({ ...prev, [field]: formatted }));

    // When we have 7 digits (XXXX-XXX), lookup address
    if (digits.length === 7) {
      // 1) Tentar cache local (sessionStorage) para evitar nova chamada à API
      try {
        const cacheKey = `geoapi_cp_${formatted}`;
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          const data = JSON.parse(cached);
          setFormData(prev => ({
            ...prev,
            [localidadeField]: data.localidade || prev[localidadeField],
            ...(data.arteria && !prev[moradaField] ? { [moradaField]: data.arteria } : {}),
          }));
          if (data.arteria && data.localidade) {
            toast.success(`Codigo postal (cache): ${data.arteria}, ${data.localidade}`);
          } else if (data.localidade) {
            toast.info(`Localidade preenchida (cache): ${data.localidade}.`);
          }
          return;
        }
      } catch { /* ignore cache errors */ }

      // 2) Backoff — se recentemente recebemos 429, evitar nova chamada
      try {
        const blockedUntil = parseInt(sessionStorage.getItem('geoapi_blocked_until') || '0', 10);
        if (blockedUntil && Date.now() < blockedUntil) {
          const segs = Math.ceil((blockedUntil - Date.now()) / 1000);
          toast.warning(`API de Códigos Postais bloqueada por mais ${segs}s. Escreve a morada manualmente.`);
          return;
        }
      } catch { /* ignore */ }

      try {
        const res = await fetch(`${API_URL}/postal-code/${formatted}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        });
        if (res.status === 429) {
          // Rate limited — bloqueia chamadas durante 60 segundos
          try { sessionStorage.setItem('geoapi_blocked_until', String(Date.now() + 60_000)); } catch { /* ignore */ }
          toast.error('API de Códigos Postais saturada (429). Escreve a morada manualmente — vai voltar a funcionar dentro de 1 min.', { duration: 6000 });
          return;
        }
        if (res.ok) {
          const data = await res.json();
          const localidade = (data.localidade || '').toUpperCase();
          const arteriaUpper = (data.arteria || '').toUpperCase();
          // Cache result local (sessionStorage) — mais rápido que ir ao backend
          try {
            sessionStorage.setItem(`geoapi_cp_${formatted}`, JSON.stringify({ localidade, arteria: arteriaUpper }));
          } catch { /* ignore */ }
          setFormData(prev => ({
            ...prev,
            [localidadeField]: localidade || prev[localidadeField],
            ...(arteriaUpper && !prev[moradaField] ? { [moradaField]: arteriaUpper } : {}),
          }));
          const sourceLabel = data.source === 'cache' ? ' (cache)' : '';
          if (arteriaUpper && localidade) {
            toast.success(`Codigo postal${sourceLabel}: ${arteriaUpper}, ${localidade}`);
          } else if (localidade) {
            toast.info(`Localidade preenchida${sourceLabel}: ${localidade}. Morada nao disponivel para este codigo - escreva manualmente.`);
          } else {
            toast.warning(`Codigo postal valido mas sem dados de morada. Escreva manualmente.`);
          }
        } else {
          toast.error('Codigo postal invalido. Verifique e tente novamente.');
        }
      } catch {
        toast.error('Erro ao consultar codigo postal. Escreva manualmente.');
      }
    }
  };

  const addTelefone = () => {
    setFormData(prev => ({
      ...prev,
      telefones: [...prev.telefones, { tipo: 'telemovel', numero: '' }]
    }));
  };

  const removeTelefone = (index) => {
    setFormData(prev => ({
      ...prev,
      telefones: prev.telefones.filter((_, i) => i !== index)
    }));
  };

  const updateTelefone = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      telefones: prev.telefones.map((t, i) => i === index ? { ...t, [field]: value } : t)
    }));
  };

  const addEmail = () => {
    setFormData(prev => ({
      ...prev,
      emails: [...prev.emails, '']
    }));
  };

  const removeEmail = (index) => {
    setFormData(prev => ({
      ...prev,
      emails: prev.emails.filter((_, i) => i !== index)
    }));
  };

  const updateEmail = (index, value) => {
    setFormData(prev => ({
      ...prev,
      emails: prev.emails.map((e, i) => i === index ? value.toLowerCase() : e)
    }));
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  // Reusable compact inline banner shown right after CPE/CUI inputs (single instance per form)
  const duplicateInlineBanner = duplicateWarning?.duplicate ? (
    <div
      className="col-span-full flex items-start gap-3 p-3 rounded-lg border border-amber-300 bg-amber-50"
      data-testid="duplicate-inline-banner"
    >
      <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1 text-sm">
        <p className="font-semibold text-amber-900">
          ⚠️ Já existe um contrato com este NIF e CPE/CUI
        </p>
        <p className="text-amber-800 text-xs mt-0.5">
          #{duplicateWarning.duplicate.codigo} · {duplicateWarning.duplicate.nome_cliente || '—'} ·{' '}
          {COMPANY_NAMES[duplicateWarning.duplicate.company] || duplicateWarning.duplicate.company} ·{' '}
          {duplicateWarning.diff_days === 0 ? 'mesma data' : `há ${duplicateWarning.diff_days}d`}{' '}
          (estado: {duplicateWarning.duplicate.status})
          {' · '}
          <a
            href={`/contracts/${duplicateWarning.duplicate.service_category}/${duplicateWarning.duplicate.company}/${duplicateWarning.duplicate.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-amber-900 underline font-medium hover:text-amber-700"
            data-testid="duplicate-inline-view"
          >
            Ver em nova tab ↗
          </a>
        </p>
        {!duplicateWarning.can_force && (
          <p className="text-[11px] text-amber-700 mt-1">
            💡 Solicita anulação do contrato existente antes de criar este, ou pede a um Gestor/Super Admin para forçar.
          </p>
        )}
      </div>
    </div>
  ) : null;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="contract-form-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={goBack}
            className="rounded-full"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-outfit">
              {isEditing ? `Editar ${companyName}` : `Criar ${companyName}`}
            </h1>
            <p className="text-slate-500 mt-1">
              {isEditing ? 'Atualize os dados do contrato' : 'Preencha os dados do novo contrato'}
            </p>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          onKeyDown={(e) => {
            // Prevent Enter from submitting the form when typing in inputs/selects.
            // Allow Enter inside <textarea> for multi-line notes.
            if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
              e.preventDefault();
            }
          }}
        >
          {/* Lead link banner */}
          {formData.lead_id && (
            <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-2 text-sm text-emerald-800" data-testid="from-lead-banner">
              <Link2 className="w-4 h-4 shrink-0" />
              <span>Este contrato sera criado a partir de uma <strong>Lead</strong>. O angariador sera automaticamente associado.</span>
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Tipo de Segmento / Autorizações */}
              <Card className="rounded-2xl border-slate-100">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="text-base font-semibold">Tipo de Segmento / Autorizações</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-2 gap-6">
                    {/* Contratação - Solar only */}
                    {isSolar && (
                      <div>
                        <Label>Contratação *</Label>
                        <Select value={formData.contratacao} onValueChange={(v) => handleChange('contratacao', v)}>
                          <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="contratacao-select">
                            <SelectValue placeholder="Selecionar" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="digital">Contratação Digital</SelectItem>
                            <SelectItem value="papel">Contratação em Papel</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div>
                      <Label>Tipo Segmento *</Label>
                      <Select value={formData.tipo_segmento} onValueChange={(v) => handleChange('tipo_segmento', v)}>
                        <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="tipo-segmento-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="residencial">Residencial</SelectItem>
                          <SelectItem value="empresarial">Empresarial</SelectItem>
                          <SelectItem value="condominio">Condomínio</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label>Tipo Produto *</Label>
                      {isSolar ? (
                        <div className="mt-1.5 px-3 py-2.5 h-11 bg-slate-100 rounded-lg border text-sm text-slate-700 flex items-center">
                          Painéis Solares
                        </div>
                      ) : isTelecom ? (
                        <Select value={formData.tipo_produto} onValueChange={(v) => handleChange('tipo_produto', v)}>
                          <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="tipo-produto-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1p">1P</SelectItem>
                            <SelectItem value="2p">2P</SelectItem>
                            <SelectItem value="3p">3P</SelectItem>
                            <SelectItem value="4p">4P</SelectItem>
                            <SelectItem value="5p">5P</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : isAlarme ? (
                        <Select value={formData.tipo_produto} onValueChange={(v) => handleChange('tipo_produto', v)}>
                          <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="tipo-produto-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="alarme_residencial">Residencial</SelectItem>
                            <SelectItem value="alarme_comercial">Comercial</SelectItem>
                            <SelectItem value="alarme_industrial">Industrial</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Select value={formData.tipo_produto} onValueChange={(v) => handleChange('tipo_produto', v)}>
                          <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="tipo-produto-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="luz">Luz</SelectItem>
                            {company !== 'yes' && <SelectItem value="luz_gas">Luz + Gás</SelectItem>}
                            {company !== 'yes' && <SelectItem value="gas">Gás</SelectItem>}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    
                    {/* Controlo de Qualidade - Solar only */}
                    {isSolar && (
                      <div>
                        <Label>Controlo de Qualidade</Label>
                        <Input
                          value={formData.controlo_qualidade}
                          onChange={(e) => handleChange('controlo_qualidade', e.target.value)}
                          className="mt-1.5 h-11 rounded-lg"
                          data-testid="controlo-qualidade-input"
                        />
                      </div>
                    )}

                    <div>
                      <Label htmlFor="localidade" className="flex items-center gap-1.5">
                        Localidade
                        <span className="text-[10px] font-normal text-slate-400">🔒 auto-preenchida pela Morada de Fornecimento</span>
                      </Label>
                      <Input
                        id="localidade"
                        value={formData.localidade}
                        readOnly
                        disabled
                        placeholder=""
                        className="mt-1.5 h-11 rounded-lg bg-slate-100 cursor-not-allowed text-slate-600"
                        data-testid="localidade-topo-readonly-input"
                      />
                    </div>
                    
                    <div>
                      <Label>Indoor</Label>
                      <Select value={formData.indoor} onValueChange={(v) => handleChange('indoor', v)}>
                        <SelectTrigger className="mt-1.5 h-11 rounded-lg">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="nao">Não</SelectItem>
                          <SelectItem value="sim">Sim</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Checkboxes - only for Energias and Carregamento */}
                  {!isSolar && !isTelecom && (
                    <div className="grid grid-cols-3 gap-6 mt-6 pt-6 border-t border-slate-100">
                      <div>
                        <p className="text-sm text-slate-500 mb-3">Luz</p>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox checked={formData.luz_mc} onCheckedChange={(c) => handleChange('luz_mc', c)} />
                            M.C
                          </label>
                          <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox checked={formData.luz_at} onCheckedChange={(c) => handleChange('luz_at', c)} />
                            A.T
                          </label>
                          <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox checked={formData.luz_ed} onCheckedChange={(c) => handleChange('luz_ed', c)} />
                            E.D
                          </label>
                        </div>
                      </div>
                      {!isCarregamento && (
                        <div>
                          <p className="text-sm text-slate-500 mb-3">Gás</p>
                          <div className="space-y-2">
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                              <Checkbox checked={formData.gas_mc} onCheckedChange={(c) => handleChange('gas_mc', c)} />
                              M.C
                            </label>
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                              <Checkbox checked={formData.gas_at} onCheckedChange={(c) => handleChange('gas_at', c)} />
                              A.T
                            </label>
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                              <Checkbox checked={formData.gas_ed} onCheckedChange={(c) => handleChange('gas_ed', c)} />
                              E.D
                            </label>
                          </div>
                        </div>
                      )}
                      <div>
                        <p className="text-sm text-slate-500 mb-3">Cartão Cidadão</p>
                        <Select value={formData.cartao_cidadao} onValueChange={(v) => handleChange('cartao_cidadao', v)}>
                          <SelectTrigger className="h-10 rounded-lg">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sim">Sim</SelectItem>
                            <SelectItem value="nao">Não</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  {/* Solar: Contratação Energia */}
                  {isSolar && (
                    <div className="grid grid-cols-2 gap-6 mt-6 pt-6 border-t border-slate-100">
                      <div>
                        <Label>Contratação Energia</Label>
                        <Select value={formData.contratacao_energia} onValueChange={(v) => handleChange('contratacao_energia', v)}>
                          <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="contratacao-energia-select">
                            <SelectValue placeholder="Selecionar" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sim">Sim</SelectItem>
                            <SelectItem value="nao">Não</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500 mb-3">Cartão Cidadão</p>
                        <Select value={formData.cartao_cidadao} onValueChange={(v) => handleChange('cartao_cidadao', v)}>
                          <SelectTrigger className="h-10 rounded-lg">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sim">Sim</SelectItem>
                            <SelectItem value="nao">Não</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Estado da Venda */}
              <Card className="rounded-2xl border-slate-100">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="text-base font-semibold">Estado da Venda</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div>
                      <Label>Estado</Label>
                      <div className="mt-2">
                        {isManager() ? (
                          <Select value={formData.status} onValueChange={(v) => handleChange('status', v)}>
                            <SelectTrigger className="w-64 h-11 rounded-lg" data-testid="status-select">
                              <SelectValue>
                                <StatusBadge status={formData.status} />
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {statusOptions.map(s => (
                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <div className="flex items-center gap-2">
                            <StatusBadge status={formData.status} />
                            <span className="text-xs text-slate-400">(Apenas gestores podem alterar)</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="data_assinatura">Data Assinatura</Label>
                        <Input
                          id="data_assinatura"
                          type="date"
                          value={formData.data_assinatura}
                          onChange={(e) => handleChange('data_assinatura', e.target.value)}
                          className="mt-1.5 h-11 rounded-lg"
                        />
                      </div>
                      <div>
                        <Label htmlFor="agendamento">Agendamento</Label>
                        <Input
                          id="agendamento"
                          type="datetime-local"
                          value={formData.agendamento}
                          onChange={(e) => handleChange('agendamento', e.target.value)}
                          className="mt-1.5 h-11 rounded-lg"
                        />
                      </div>
                    </div>

                    {/* Solar-specific dates */}
                    {isSolar && (
                      <>
                        <div className="grid grid-cols-3 gap-6">
                          <div>
                            <Label>Data Contrato</Label>
                            <Input type="date" value={formData.data_contrato} onChange={(e) => handleChange('data_contrato', e.target.value)} className="mt-1.5 h-11 rounded-lg" data-testid="data-contrato-input" />
                          </div>
                          <div>
                            <Label>Data de Registo</Label>
                            <Input type="date" value={formData.data_registo} onChange={(e) => handleChange('data_registo', e.target.value)} className="mt-1.5 h-11 rounded-lg" data-testid="data-registo-input" />
                          </div>
                          <div>
                            <Label>Data Instalação</Label>
                            <Input type="date" value={formData.data_instalacao} onChange={(e) => handleChange('data_instalacao', e.target.value)} className="mt-1.5 h-11 rounded-lg" data-testid="data-instalacao-input" />
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-6">
                          <div>
                            <Label>Data Ativação</Label>
                            <Input type="date" value={formData.data_ativacao} onChange={(e) => handleChange('data_ativacao', e.target.value)} className="mt-1.5 h-11 rounded-lg" data-testid="data-ativacao-input" />
                          </div>
                          <div>
                            <Label>Data Baixa</Label>
                            <Input type="date" value={formData.data_baixa} onChange={(e) => handleChange('data_baixa', e.target.value)} className="mt-1.5 h-11 rounded-lg" data-testid="data-baixa-input" />
                          </div>
                        </div>
                        {/* Campos Financeiros */}
                        {['super_admin', 'gestor'].includes(user?.role) && (
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            <div>
                              <Label>Receita Empresa (EUR)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={formData.receita_empresa || ''}
                                onChange={(e) => handleChange('receita_empresa', e.target.value)}
                                placeholder="Sistema"
                                className="mt-1.5 h-11 rounded-lg"
                                data-testid="receita-empresa-input"
                              />
                            </div>
                            <div>
                              <Label>Comissao Comercial (EUR)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={formData.valor_comissao}
                                onChange={(e) => handleChange('valor_comissao', e.target.value)}
                                placeholder="Sistema"
                                className="mt-1.5 h-11 rounded-lg"
                                data-testid="valor-comissao-input"
                              />
                            </div>
                            <div>
                              <Label>Comissao Parceiro (EUR)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={formData.comissao_parceiro || ''}
                                onChange={(e) => handleChange('comissao_parceiro', e.target.value)}
                                placeholder="Sistema"
                                className="mt-1.5 h-11 rounded-lg"
                                data-testid="comissao-parceiro-input"
                              />
                            </div>
                            <div>
                              <Label>Estado Pagamento</Label>
                              <Select value={formData.estado_pagamento || 'pendente'} onValueChange={(v) => handleChange('estado_pagamento', v)}>
                                <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="estado-pagamento-select">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pendente">Pendente</SelectItem>
                                  <SelectItem value="a pagamento">A Pagamento</SelectItem>
                                  <SelectItem value="pago">Pago</SelectItem>
                                  <SelectItem value="anulado">Anulado</SelectItem>
                                  <SelectItem value="baixa">Baixa</SelectItem>
                                  <SelectItem value="chargeback">Chargeback</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-6">
                          <div>
                            <Label>Nº Lead</Label>
                            <Input value={formData.nr_lead} onChange={(e) => handleChange('nr_lead', e.target.value)} className="mt-1.5 h-11 rounded-lg" data-testid="nr-lead-input" />
                          </div>
                          <div>
                            <Label>OFFER</Label>
                            <Input value={formData.offer} onChange={(e) => handleChange('offer', e.target.value)} className="mt-1.5 h-11 rounded-lg" data-testid="offer-input" />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Dados Cliente / Empresa */}
              <Card className="rounded-2xl border-slate-100">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="text-base font-semibold">Dados Cliente / Empresa</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="nome_cliente">Nome Cliente / Empresa *</Label>
                      <Input
                        id="nome_cliente"
                        value={formData.nome_cliente}
                        onChange={(e) => handleChange('nome_cliente', e.target.value)}
                        placeholder="Nome completo"
                        className="mt-1.5 h-11 rounded-lg"
                        required
                        data-testid="nome-cliente-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="nif">NIF *</Label>
                      <Input
                        id="nif"
                        value={formData.nif}
                        onChange={(e) => handleChange('nif', e.target.value)}
                        placeholder="123456789"
                        className="mt-1.5 h-11 rounded-lg"
                        data-testid="nif-input"
                        maxLength={9}
                      />
                      {formData.nif && !validateNIF(formData.nif).ok && (
                        <p className="text-xs text-rose-600 mt-1" data-testid="nif-error">
                          {validateNIF(formData.nif).error}
                        </p>
                      )}
                      {nifNameWarning?.exists && (
                        nifNameWarning.same_name ? (
                          <p className="text-xs text-emerald-600 mt-1.5 flex items-start gap-1" data-testid="nif-same-client">
                            <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                            <span>Mesmo cliente já existente ({nifNameWarning.existing?.nome_cliente} · #{nifNameWarning.existing?.codigo}). Permitido para outro serviço.</span>
                          </p>
                        ) : (
                          <div className="mt-1.5 space-y-1" data-testid="nif-conflict-inline">
                            <p className="text-xs text-rose-600 font-medium flex items-start gap-1">
                              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                              <span>
                                Este NIF já pertence a <strong>{nifNameWarning.existing?.nome_cliente || '—'}</strong> (contrato #{nifNameWarning.existing?.codigo}).
                                {' '}Um NIF pertence a uma só pessoa.
                              </span>
                            </p>
                            {formData.nif_comprovativo && !nifBlocked && (
                              <p className="text-xs text-amber-700 flex items-start gap-1" data-testid="nif-comprovativo-ok">
                                <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                                <span>Comprovativo confirmado — o contrato será criado como <strong>DIVERGENTE</strong>.</span>
                              </p>
                            )}
                            {nifBlocked && (
                              <p className="text-xs text-rose-700 font-semibold flex items-start gap-1" data-testid="nif-blocked-msg">
                                <X className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                                <span>Não é possível continuar sem comprovativo do NIF. </span>
                                <button type="button" className="underline" onClick={() => { promptedNifRef.current = null; setNifBlocked(false); setNifCheckStep('step1'); }} data-testid="nif-retry-btn">
                                  Responder de novo
                                </button>
                              </p>
                            )}
                          </div>
                        )
                      )}
                      {company === 'iberdrola' && iberdrolaChecking && (
                        <p className="text-xs text-slate-400 mt-1.5" data-testid="iberdrola-checking">A verificar elegibilidade Iberdrola...</p>
                      )}
                      {company === 'iberdrola' && iberdrolaCheck && !iberdrolaChecking && (
                        <div
                          className={`mt-1.5 flex items-start gap-2 px-3 py-2 rounded-lg text-xs ${
                            iberdrolaCheck.eligible
                              ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                              : 'bg-red-50 border border-red-200 text-red-800'
                          }`}
                          data-testid="iberdrola-check-result"
                        >
                          <span className="font-bold">{iberdrolaCheck.eligible ? '✅' : '⚠️'}</span>
                          <span className="flex-1 leading-relaxed">{iberdrolaCheck.message}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Comercializadora Anterior */}
                  <div className="grid grid-cols-2 gap-6 mt-6">
                    <div>
                      <Label htmlFor="comercializadora_anterior">Comercializadora Anterior</Label>
                      <Select
                        value={formData.comercializadora_anterior || ''}
                        onValueChange={(v) => handleChange('comercializadora_anterior', v)}
                      >
                        <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="comercializadora-anterior-select">
                          <SelectValue placeholder="Selecionar..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="primeira_contratacao">Primeira contratação</SelectItem>
                          <SelectItem value="outra">Não sei / Outra</SelectItem>
                          {prevCompanies.map(pc => (
                            <SelectItem key={pc.slug} value={pc.slug}>{pc.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-400 mt-1">Onde estava o cliente antes deste contrato</p>
                    </div>
                  </div>

                  {/* CAE / CRC - Solar only */}
                  {isSolar && (
                    <div className="grid grid-cols-2 gap-6 mt-6">
                      <div>
                        <Label>CAE</Label>
                        <Input
                          value={formData.cae}
                          onChange={(e) => handleChange('cae', e.target.value)}
                          className="mt-1.5 h-11 rounded-lg"
                          data-testid="cae-input"
                        />
                      </div>
                      <div>
                        <Label>CRC</Label>
                        <Input
                          value={formData.crc}
                          onChange={(e) => handleChange('crc', e.target.value)}
                          className="mt-1.5 h-11 rounded-lg"
                          data-testid="crc-input"
                        />
                      </div>
                    </div>
                  )}
                  
                  <div className="mt-6">
                    <Label>Telefone *</Label>
                    <div className="space-y-2 mt-1.5">
                      {formData.telefones.map((tel, index) => {
                        const phCheck = tel.numero ? validatePhone(tel.numero, tel.tipo) : { ok: true };
                        return (
                        <div key={index}>
                        <div className="flex gap-2">
                          <Select value={tel.tipo} onValueChange={(v) => updateTelefone(index, 'tipo', v)}>
                            <SelectTrigger className="w-32 h-11 rounded-lg" data-testid={`telefone-tipo-${index}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="telemovel">Telemóvel</SelectItem>
                              <SelectItem value="fixo">Fixo</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input
                            value={tel.numero}
                            onChange={(e) => updateTelefone(index, 'numero', e.target.value)}
                            placeholder={tel.tipo === 'fixo' ? '211 234 567' : '912 345 678'}
                            className="flex-1 h-11 rounded-lg"
                            data-testid={`telefone-input-${index}`}
                            maxLength={9}
                          />
                          {index > 0 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeTelefone(index)}
                              className="h-11 w-11"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        {!phCheck.ok && (
                          <p className="text-xs text-rose-600 mt-1" data-testid={`telefone-error-${index}`}>
                            {phCheck.error}
                          </p>
                        )}
                        </div>
                        );
                      })}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addTelefone}
                        className="gap-1"
                      >
                        <Plus className="w-3 h-3" />
                        Adicionar telefone
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Dados Produto */}
              <Card className="rounded-2xl border-slate-100">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="text-base font-semibold">
                    {(isTelecom || isSolar || isAlarme) ? 'Morada de Instalação' : 'Morada de Fornecimento'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Codigo Postal</Label>
                      <Input
                        value={formData.codigo_postal_fornecimento}
                        onChange={(e) => handlePostalCode('codigo_postal_fornecimento', e.target.value, 'morada_fornecimento', 'localidade_fornecimento')}
                        placeholder=""
                        className="mt-1.5 h-11 rounded-lg"
                        data-testid="codigo-postal-fornecimento-input"
                        maxLength={8}
                      />
                      <p className="text-xs text-slate-400 mt-1">Preenche automaticamente morada e localidade</p>
                    </div>
                    <div>
                      <Label>Localidade</Label>
                      <Input
                        value={formData.localidade_fornecimento}
                        onChange={(e) => handleChange('localidade_fornecimento', e.target.value)}
                        placeholder=""
                        className="mt-1.5 h-11 rounded-lg"
                        data-testid="localidade-fornecimento-input"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Morada</Label>
                      <Input
                        value={formData.morada_fornecimento}
                        onChange={(e) => handleChange('morada_fornecimento', e.target.value)}
                        placeholder="Rua, Avenida..."
                        className="mt-1.5 h-11 rounded-lg"
                        data-testid="morada-fornecimento-input"
                      />
                    </div>
                    <div>
                      <Label>Nr Porta</Label>
                      <Input
                        value={formData.nr_porta_fornecimento}
                        onChange={(e) => handleChange('nr_porta_fornecimento', e.target.value)}
                        placeholder=""
                        className="mt-1.5 h-11 rounded-lg"
                        data-testid="nr-porta-fornecimento-input"
                      />
                    </div>
                    <div>
                      <Label>Andar</Label>
                      <Input
                        value={formData.andar_fornecimento}
                        onChange={(e) => handleChange('andar_fornecimento', e.target.value)}
                        placeholder=""
                        className="mt-1.5 h-11 rounded-lg"
                        data-testid="andar-fornecimento-input"
                      />
                    </div>
                    <div>
                      <Label>Lado</Label>
                      <Input
                        value={formData.lado_fornecimento}
                        onChange={(e) => handleChange('lado_fornecimento', e.target.value)}
                        placeholder="Esq / Dto"
                        className="mt-1.5 h-11 rounded-lg"
                        data-testid="lado-fornecimento-input"
                      />
                    </div>
                  </div>

                  {/* Toggle: Morada de faturação igual? */}
                  <div className="mt-6 pt-6 border-t border-slate-100">
                    <div className="flex items-center gap-3">
                      <Label className="text-sm font-medium">Morada de faturação é igual à {(isTelecom || isSolar || isAlarme) ? 'morada de instalação' : 'morada de fornecimento'}?</Label>
                      <Select value={formData.morada_faturacao_igual} onValueChange={(v) => handleChange('morada_faturacao_igual', v)}>
                        <SelectTrigger className="w-24 h-9 rounded-lg" data-testid="morada-faturacao-igual-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sim">Sim</SelectItem>
                          <SelectItem value="nao">Não</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Morada de Faturação - conditional */}
                  {formData.morada_faturacao_igual === 'nao' && (
                    <div className="mt-6 pt-6 border-t border-slate-100">
                      <p className="text-sm font-semibold text-slate-700 mb-4">Morada de Faturação</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Codigo Postal</Label>
                          <Input
                            value={formData.codigo_postal_faturacao}
                            onChange={(e) => handlePostalCode('codigo_postal_faturacao', e.target.value, 'morada_faturacao', 'localidade_faturacao')}
                            placeholder=""
                            className="mt-1.5 h-11 rounded-lg"
                            data-testid="codigo-postal-faturacao-input"
                            maxLength={8}
                          />
                          <p className="text-xs text-slate-400 mt-1">Preenche automaticamente morada e localidade</p>
                        </div>
                        <div>
                          <Label>Localidade</Label>
                          <Input
                            value={formData.localidade_faturacao}
                            onChange={(e) => handleChange('localidade_faturacao', e.target.value)}
                            placeholder=""
                            className="mt-1.5 h-11 rounded-lg"
                            data-testid="localidade-faturacao-input"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label>Morada</Label>
                          <Input
                            value={formData.morada_faturacao}
                            onChange={(e) => handleChange('morada_faturacao', e.target.value)}
                            placeholder="Rua, Avenida..."
                            className="mt-1.5 h-11 rounded-lg"
                            data-testid="morada-faturacao-input"
                          />
                        </div>
                        <div>
                          <Label>Nr Porta</Label>
                          <Input
                            value={formData.nr_porta_faturacao}
                            onChange={(e) => handleChange('nr_porta_faturacao', e.target.value)}
                            placeholder=""
                            className="mt-1.5 h-11 rounded-lg"
                            data-testid="nr-porta-faturacao-input"
                          />
                        </div>
                        <div>
                          <Label>Andar</Label>
                          <Input
                            value={formData.andar_faturacao}
                            onChange={(e) => handleChange('andar_faturacao', e.target.value)}
                            placeholder=""
                            className="mt-1.5 h-11 rounded-lg"
                            data-testid="andar-faturacao-input"
                          />
                        </div>
                        <div>
                          <Label>Lado</Label>
                          <Input
                            value={formData.lado_faturacao}
                            onChange={(e) => handleChange('lado_faturacao', e.target.value)}
                            placeholder="Esq / Dto"
                            className="mt-1.5 h-11 rounded-lg"
                            data-testid="lado-faturacao-input"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Dados Produto / Telecom / Carregamento */}
              {!isTelecom && (
              <Card className="rounded-2xl border-slate-100">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="text-base font-semibold">Dados Produto</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {isSolar ? (
                    /* Solar-specific */
                    <div className="space-y-6">
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <Label>Tipo de Painel *</Label>
                          <Select value={formData.tipo_painel} onValueChange={(v) => handleChange('tipo_painel', v)}>
                            <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="tipo-painel-select">
                              <SelectValue placeholder="Selecionar" />
                            </SelectTrigger>
                            <SelectContent>
                              {solarPanels.map(p => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Microinversor</Label>
                          <Select value={formData.microinversor} onValueChange={(v) => handleChange('microinversor', v)}>
                            <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="microinversor-select">
                              <SelectValue placeholder="Selecionar" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="sim">Sim</SelectItem>
                              <SelectItem value="nao">Não</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <Label>Baterias</Label>
                          <Select value={formData.baterias} onValueChange={(v) => handleChange('baterias', v)}>
                            <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="baterias-select">
                              <SelectValue placeholder="Selecionar" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="sim">Sim</SelectItem>
                              <SelectItem value="nao">Não</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Número Painéis Solares *</Label>
                          <Input type="number" min="1" value={formData.numero_paineis_solares} onChange={(e) => handleChange('numero_paineis_solares', e.target.value)} placeholder="" className="mt-1.5 h-11 rounded-lg" data-testid="numero-paineis-input" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <Label>Método de Pagamento *</Label>
                          <Select value={formData.metodo_pagamento} onValueChange={(v) => handleChange('metodo_pagamento', v)}>
                            <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="metodo-pagamento-select">
                              <SelectValue placeholder="Selecionar" />
                            </SelectTrigger>
                            <SelectContent>
                              {solarPayments.map(p => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-6 pt-6 border-t border-slate-100">
                        <div>
                          <Label htmlFor="cpe">CPE</Label>
                          <div className="mt-1.5">
                            <FormattedInput prefix="PT0002" value={formData.cpe} onChange={(v) => handleChange('cpe', v)} numDigits={12} numLetters={2} placeholder="0000 0413 6941 CV" id="cpe" data-testid="cpe-input" />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="cui">CUI</Label>
                          <div className="mt-1.5">
                            <FormattedInput prefix="PT1" value={formData.cui} onChange={(v) => handleChange('cui', v)} numDigits={15} numLetters={2} groups={[3,4,4,4]} placeholder="605 0000 0987 6654 TW" id="cui" data-testid="cui-input" />
                          </div>
                        </div>
                        {duplicateInlineBanner}
                      </div>
                    </div>
                  ) : isCarregamento ? (
                    /* Carregamento Elétrico */
                    <div className="space-y-6">
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <Label>Produto *</Label>
                          <Select value={formData.produto_carregador} onValueChange={(v) => handleChange('produto_carregador', v)}>
                            <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="produto-carregador-select">
                              <SelectValue placeholder="Selecionar" />
                            </SelectTrigger>
                            <SelectContent>
                              {chargerProducts.map(p => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Potência do Carregador *</Label>
                          <Select value={formData.potencia_carregador} onValueChange={(v) => handleChange('potencia_carregador', v)}>
                            <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="potencia-carregador-select">
                              <SelectValue placeholder="Selecionar" />
                            </SelectTrigger>
                            <SelectContent>
                              {CHARGER_POWER.map(p => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      {/* CPE only for Carregamento (no CUI) */}
                      <div className="grid grid-cols-2 gap-6 pt-6 border-t border-slate-100">
                        <div>
                          <Label htmlFor="cpe">CPE</Label>
                          <div className="mt-1.5">
                            <FormattedInput prefix="PT0002" value={formData.cpe} onChange={(v) => handleChange('cpe', v)} numDigits={12} numLetters={2} placeholder="0000 0413 6941 CV" id="cpe" data-testid="cpe-input" />
                          </div>
                        </div>
                        {duplicateInlineBanner}
                      </div>
                    </div>
                  ) : (
                    /* Energy Dados Produto */
                    <div>
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <Label>Campanha *</Label>
                          <Select value={formData.campanha} onValueChange={(v) => handleChange('campanha', v)}>
                            <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="campanha-select">
                              <SelectValue placeholder="Selecionar campanha" />
                            </SelectTrigger>
                            <SelectContent>
                              {campaigns.length > 0 ? campaigns.map(c => (<SelectItem key={c} value={c}>{c}</SelectItem>)) : (<SelectItem value="default">Campanha Padrão</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>SVA</Label>
                          <Select value={formData.sva} onValueChange={(v) => handleChange('sva', v)}>
                            <SelectTrigger className="mt-1.5 h-11 rounded-lg"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="sim">Sim</SelectItem>
                              <SelectItem value="nao">Não</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      {(formData.tipo_produto === 'luz' || formData.tipo_produto === 'luz_gas' || formData.tipo_produto === 'gas') && (
                        <div className="grid grid-cols-2 gap-6 mt-6 pt-6 border-t border-slate-100">
                          {(formData.tipo_produto === 'luz' || formData.tipo_produto === 'luz_gas') && (
                            <div>
                              <Label htmlFor="cpe">CPE</Label>
                              <div className="mt-1.5">
                                <FormattedInput prefix="PT0002" value={formData.cpe} onChange={(v) => handleChange('cpe', v)} numDigits={12} numLetters={2} placeholder="0000 0413 6941 CV" id="cpe" data-testid="cpe-input" />
                              </div>
                            </div>
                          )}
                          {(formData.tipo_produto === 'gas' || formData.tipo_produto === 'luz_gas') && (
                            <div>
                              <Label htmlFor="cui">CUI</Label>
                              <div className="mt-1.5">
                                <FormattedInput prefix="PT1" value={formData.cui} onChange={(v) => handleChange('cui', v)} numDigits={15} numLetters={2} groups={[3,4,4,4]} placeholder="605 0000 0987 6654 TW" id="cui" data-testid="cui-input" />
                              </div>
                            </div>
                          )}
                          {duplicateInlineBanner}
                        </div>
                      )}
                      {(formData.tipo_produto === 'luz' || formData.tipo_produto === 'luz_gas') && (
                        <div className="mt-6">
                          <Label htmlFor="potencia">Potência Contratada <span className="text-rose-500">*</span></Label>
                          <Select value={formData.potencia} onValueChange={(v) => handleChange('potencia', v)}>
                            <SelectTrigger className={`mt-1.5 h-11 rounded-lg ${formData.potencia && parseFloat(formData.potencia) < 3.45 ? 'border-rose-400 text-rose-700' : ''}`} data-testid="potencia-select">
                              <SelectValue placeholder="Selecionar potência (kVA)..." />
                            </SelectTrigger>
                            <SelectContent>
                              {['1.15', '2.30', '3.45', '4.60', '5.75', '6.90', '10.35', '13.80', '17.25', '20.70', '27.60', '34.50', '41.40'].map(p => (
                                <SelectItem key={p} value={p}>
                                  {p} kVA {parseFloat(p) < 3.45 ? '⚠️ (não comissionada)' : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {formData.potencia && parseFloat(formData.potencia) < 3.45 && (
                            <p className="text-xs text-rose-600 mt-1 font-medium">⚠️ Potência inferior a 3,45 kVA — este contrato não é comissionado.</p>
                          )}
                        </div>
                      )}
                      {(formData.tipo_produto === 'gas' || formData.tipo_produto === 'luz_gas') && (
                        <div className="mt-6">
                          <Label htmlFor="escalao_gas">Escalão de Gás <span className="text-rose-500">*</span></Label>
                          <Select value={formData.escalao_gas || ''} onValueChange={(v) => handleChange('escalao_gas', v)}>
                            <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="escalao-gas-select">
                              <SelectValue placeholder="Selecionar escalão..." />
                            </SelectTrigger>
                            <SelectContent>
                              {[['1', 'até 220 m³/ano'], ['2', '221 a 500 m³/ano'], ['3', '501 a 1 000 m³/ano'], ['4', '1 001 a 10 000 m³/ano']].map(([v, d]) => (
                                <SelectItem key={v} value={v} data-testid={`escalao-gas-option-${v}`}>Escalão {v} ({d})</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
              )}

              {/* Telecomunicações-specific fields */}
              {isTelecom && (
              <Card className="rounded-2xl border-slate-100">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="text-base font-semibold">Dados Telecomunicações</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <Label>Portabilidade / Rescisão</Label>
                        <div className="mt-2 space-y-2">
                          <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox checked={formData.portabilidade === 'portabilidade'} onCheckedChange={(c) => handleChange('portabilidade', c ? 'portabilidade' : '')} />
                            Portabilidade
                          </label>
                          <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox checked={formData.portabilidade === 'rescisao'} onCheckedChange={(c) => handleChange('portabilidade', c ? 'rescisao' : '')} />
                            Rescisão
                          </label>
                        </div>
                      </div>
                      <div>
                        <Label>Pacote Adesão</Label>
                        <Input value={formData.pacote_adesao} onChange={(e) => handleChange('pacote_adesao', e.target.value)} className="mt-1.5 h-11 rounded-lg" data-testid="pacote-adesao-input" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <Label>Ofertas</Label>
                        <Input value={formData.ofertas_telecom} onChange={(e) => handleChange('ofertas_telecom', e.target.value)} className="mt-1.5 h-11 rounded-lg" data-testid="ofertas-telecom-input" />
                      </div>
                      <div>
                        <Label>Valor do Pacote</Label>
                        <Input value={formData.valor_pacote} onChange={(e) => handleChange('valor_pacote', e.target.value)} className="mt-1.5 h-11 rounded-lg" data-testid="valor-pacote-input" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              )}

              {/* Dados Alarmes */}
              {isAlarme && (
              <Card className="rounded-2xl border-slate-100">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="text-base font-semibold">Dados Alarmes</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <Label>Tipo de Alarme</Label>
                        <Select value={formData.tipo_alarme} onValueChange={(v) => handleChange('tipo_alarme', v)}>
                          <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="tipo-alarme-select">
                            <SelectValue placeholder="Selecionar tipo" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sem_fios">Sem Fios</SelectItem>
                            <SelectItem value="com_fios">Com Fios</SelectItem>
                            <SelectItem value="hibrido">Híbrido</SelectItem>
                            <SelectItem value="videovigilancia">Videovigilância</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Kit de Alarme</Label>
                        <Select value={formData.kit_alarme} onValueChange={(v) => handleChange('kit_alarme', v)}>
                          <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="kit-alarme-select">
                            <SelectValue placeholder="Selecionar kit" />
                          </SelectTrigger>
                          <SelectContent>
                            {company === 'nos_securitas' ? (
                              <>
                                <SelectItem value="kit_basico">Kit Básico</SelectItem>
                                <SelectItem value="kit_plus">Kit Plus</SelectItem>
                                <SelectItem value="kit_premium">Kit Premium</SelectItem>
                                <SelectItem value="kit_video">Kit Vídeo</SelectItem>
                              </>
                            ) : (
                              <>
                                <SelectItem value="kit_essencial">Kit Essencial</SelectItem>
                                <SelectItem value="kit_avancado">Kit Avançado</SelectItem>
                                <SelectItem value="kit_total">Kit Total</SelectItem>
                                <SelectItem value="kit_negocio">Kit Negócio</SelectItem>
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-6">
                      <div>
                        <Label>N.º Divisões</Label>
                        <Input
                          type="number"
                          min="1"
                          value={formData.n_divisoes}
                          onChange={(e) => handleChange('n_divisoes', e.target.value)}
                          placeholder="Ex: 4"
                          className="mt-1.5 h-11 rounded-lg"
                          data-testid="n-divisoes-input"
                        />
                      </div>
                      <div>
                        <Label>Período Fidelização (meses)</Label>
                        <Select value={formData.periodo_fidelizacao} onValueChange={(v) => handleChange('periodo_fidelizacao', v)}>
                          <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="periodo-fidelizacao-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="12">12 Meses</SelectItem>
                            <SelectItem value="24">24 Meses</SelectItem>
                            <SelectItem value="36">36 Meses</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Valor Mensal (EUR)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.valor_mensal_alarme}
                          onChange={(e) => handleChange('valor_mensal_alarme', e.target.value)}
                          placeholder=""
                          className="mt-1.5 h-11 rounded-lg"
                          data-testid="valor-mensal-alarme-input"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              )}

              {/* Dados Faturação */}
              <Card className="rounded-2xl border-slate-100">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="text-base font-semibold">Dados Faturação</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <Label>Fatura Eletrónica</Label>
                      <Select value={formData.fatura_eletronica} onValueChange={(v) => handleChange('fatura_eletronica', v)}>
                        <SelectTrigger className="mt-1.5 h-11 rounded-lg">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sim">Sim</SelectItem>
                          <SelectItem value="nao">Não</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {formData.fatura_eletronica === 'sim' && (
                      <div>
                        <Label>Email</Label>
                        <div className="space-y-2 mt-1.5">
                          {formData.emails.map((email, index) => (
                            <div key={index} className="flex gap-2">
                              <Input
                                type="email"
                                value={email}
                                onChange={(e) => updateEmail(index, e.target.value)}
                                placeholder="email@exemplo.com"
                                className="flex-1 h-11 rounded-lg"
                              />
                              {index > 0 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => removeEmail(index)}
                                  className="h-11 w-11"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={addEmail}
                            className="gap-1"
                          >
                            <Plus className="w-3 h-3" />
                            Adicionar email
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-6 mt-6">
                    <div>
                      <Label>Débito Direto</Label>
                      <Select value={formData.debito_direto} onValueChange={(v) => handleChange('debito_direto', v)}>
                        <SelectTrigger className="mt-1.5 h-11 rounded-lg">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sim">Sim</SelectItem>
                          <SelectItem value="nao">Não</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {formData.debito_direto === 'sim' && (
                      <div>
                        <Label htmlFor="iban">IBAN</Label>
                        <div className="mt-1.5">
                          <FormattedInput
                            prefix="PT50"
                            value={formData.iban}
                            onChange={(v) => handleChange('iban', v)}
                            numDigits={21}
                            numLetters={0}
                            placeholder="0035 0340 9876 5623 2345 2"
                            id="iban"
                            data-testid="iban-input"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Observações / Tipo de Pendência */}
              <Card className="rounded-2xl border-slate-100">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="text-base font-semibold">Observações / Tipo de Pendência</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div>
                    <Label htmlFor="tipo_pendencia">Tipo de Pendência</Label>
                    <Textarea
                      id="tipo_pendencia"
                      value={formData.tipo_pendencia}
                      onChange={(e) => handleChange('tipo_pendencia', e.target.value)}
                      placeholder="Observações adicionais..."
                      className="mt-1.5 min-h-[120px] rounded-lg"
                      data-testid="tipo-pendencia-textarea"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Documentos */}
              <Card className="rounded-2xl border-slate-100">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#2D3E6F]" />
                    Documentos
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <DocumentUpload ref={documentUploadRef} contractId={isEditing ? id : null} />
                  <ChatFilesPicker ref={chatFilesRef} contractId={isEditing ? id : null} initialFileId={isEditing ? null : new URLSearchParams(location.search).get('chat_file')} />
                </CardContent>
              </Card>

              {/* Bottom Action Buttons */}
              <div className="space-y-3 pt-2 pb-4">
                {duplicateChecking && !duplicateWarning && (
                  <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="duplicate-checking">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    A verificar duplicados…
                  </div>
                )}
                <div className="flex items-center gap-4">
                  <Button
                    type="submit"
                    disabled={saving || nifBlocked}
                    className="bg-[#2D3E6F] hover:bg-[#1a2a4a] rounded-full h-12 px-8 gap-2 text-base"
                    data-testid="gravar-button"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        A guardar...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Gravar
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={goBack}
                    className="rounded-full h-12 px-8 text-base"
                    data-testid="cancelar-button"
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Utilizador atribuído */}
              <Card className="rounded-2xl border-slate-100">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div>
                      <Label>Comercial / Proprietário *</Label>
                      {user?.role === 'comercial' ? (
                        <div className="mt-1.5 h-11 rounded-lg border border-slate-200 bg-slate-50 flex items-center px-3 text-sm text-slate-600" data-testid="assigned-to-locked">
                          {user?.name || 'Utilizador atual'}
                        </div>
                      ) : (
                        <Select value={formData.assigned_to} onValueChange={(v) => handleChange('assigned_to', v)}>
                          <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="assigned-to-select">
                            <SelectValue placeholder="Selecionar comercial" />
                          </SelectTrigger>
                          <SelectContent>
                            {users.map(u => (
                              <SelectItem key={u.id} value={u.id}>{u.name} ({u.role === 'comercial' ? 'Comercial' : u.role === 'admin' ? 'Admin' : u.role === 'gestor' ? 'Gestor' : u.role === 'backoffice' ? 'Backoffice' : u.role === 'super_admin' ? 'Super Admin' : u.role})</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    
                    <div>
                      <Label>Equipas *</Label>
                      <Select value={formData.team_id} onValueChange={(v) => handleChange('team_id', v)}>
                        <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="team-select">
                          <SelectValue placeholder="Selecionar equipa" />
                        </SelectTrigger>
                        <SelectContent>
                          {teams.map(t => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {['super_admin', 'gestor', 'admin'].includes(user?.role) && (
                      <div>
                        <Label>Parceiro <span className="text-rose-500">*</span></Label>
                        <Select value={formData.partner_id || ''} onValueChange={(v) => handleChange('partner_id', v)}>
                          <SelectTrigger className={`mt-1.5 h-11 rounded-lg ${!formData.partner_id || formData.partner_id === 'none' ? 'border-rose-300 ring-1 ring-rose-200' : ''}`} data-testid="partner-select">
                            <SelectValue placeholder="Selecionar parceiro (obrigatório)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sem parceiro</SelectItem>
                            {partners.map(p => (
                              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {(!formData.partner_id || formData.partner_id === 'none') && (
                          <p className="text-xs text-rose-500 mt-1">Obrigatório para Admin/Gestor/Super Admin</p>
                        )}
                      </div>
                    )}

                    {['super_admin', 'gestor'].includes(user?.role) && (
                      <div>
                        <Label>Estado Pagamento</Label>
                        <Select value={formData.estado_pagamento || 'pendente'} onValueChange={(v) => handleChange('estado_pagamento', v)}>
                          <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="estado-pagamento-select">
                            <SelectValue placeholder="Estado do pagamento" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pendente">Pendente</SelectItem>
                            <SelectItem value="a pagamento">A Pagamento</SelectItem>
                            <SelectItem value="pago">Pago</SelectItem>
                            <SelectItem value="anulado">Anulado</SelectItem>
                            <SelectItem value="baixa">Baixa</SelectItem>
                            <SelectItem value="chargeback">Chargeback</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex flex-col gap-3">
                <Button
                  type="submit"
                  disabled={saving || nifBlocked}
                  className="w-full bg-[#2D3E6F] hover:bg-[#1a2a4a] rounded-full h-11 gap-2"
                  data-testid="save-contract-button"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      A guardar...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Guardar
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={goBack}
                  className="w-full rounded-full h-11"
                >
                  Cancelar
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Confirmação de Atribuição (Admin/Gestor/Super Admin) */}
      <Dialog open={confirmOpen} onOpenChange={(v) => { if (!saving) setConfirmOpen(v); }}>
        <DialogContent className="sm:max-w-md" data-testid="attribution-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Confirma os dados de atribuição
            </DialogTitle>
            <DialogDescription>
              Estás a criar um contrato em nome de outra pessoa. Confirma os campos abaixo antes de gravar — depois de criado é difícil reatribuir.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2 text-sm">
            {[
              { label: 'Proprietário (Comercial)', value: users.find(u => u.id === formData.assigned_to)?.name, required: true },
              { label: 'Parceiro', value: partners.find(p => p.id === formData.partner_id)?.name, required: true },
              { label: 'Equipa', value: teams.find(t => t.id === formData.team_id)?.name, required: false },
              { label: 'Estado Pagamento', value: formData.estado_pagamento, required: false },
            ].map(({ label, value, required }) => (
              <div key={label} className={`flex items-start gap-2 p-2 rounded-lg ${value ? 'bg-emerald-50' : (required ? 'bg-rose-50' : 'bg-amber-50')}`}>
                {value
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  : <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${required ? 'text-rose-600' : 'text-amber-600'}`} />}
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-500">{label}{required ? ' *' : ''}</p>
                  <p className={`font-medium ${value ? 'text-slate-800' : (required ? 'text-rose-700' : 'text-amber-700')}`}>
                    {value || (required ? '⚠️ POR PREENCHER (obrigatório)' : '— (sem atribuição)')}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={saving} data-testid="attribution-back">
              Voltar e Rever
            </Button>
            <Button
              onClick={() => performSave()}
              disabled={saving || !formData.assigned_to || !formData.partner_id || formData.partner_id === 'none'}
              className="bg-[#2D3E6F] hover:bg-[#1a2a4a]"
              data-testid="attribution-confirm"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Confirmar e Gravar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Contract Detection Modal — blocks comercials, allows override for super_admin/gestor */}
      <Dialog open={Boolean(duplicateModal)} onOpenChange={(v) => { if (!v && !saving) setDuplicateModal(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="duplicate-contract-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5 text-rose-600" />
              Contrato duplicado detetado
            </DialogTitle>
            <DialogDescription>
              {duplicateModal?.message || 'Já existe um contrato recente com o mesmo NIF e CPE/CUI.'}
            </DialogDescription>
          </DialogHeader>
          {duplicateModal?.existing && (
            <div className="space-y-2 py-2 text-sm bg-rose-50 border border-rose-200 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                <div>
                  <p className="text-xs text-slate-500">Código</p>
                  <p className="font-mono font-semibold text-rose-800">#{duplicateModal.existing.codigo}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Estado</p>
                  <p className="font-medium text-slate-800">{duplicateModal.existing.status || '—'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-slate-500">Cliente</p>
                  <p className="font-medium text-slate-800">{duplicateModal.existing.nome_cliente || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">NIF</p>
                  <p className="font-mono text-slate-800">{duplicateModal.existing.nif || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Empresa</p>
                  <p className="font-medium text-slate-800">{COMPANY_NAMES[duplicateModal.existing.company] || duplicateModal.existing.company || '—'}</p>
                </div>
                {duplicateModal.existing.cpe && (
                  <div className="col-span-2">
                    <p className="text-xs text-slate-500">CPE</p>
                    <p className="font-mono text-slate-800">{duplicateModal.existing.cpe}</p>
                  </div>
                )}
                {duplicateModal.existing.cui && (
                  <div className="col-span-2">
                    <p className="text-xs text-slate-500">CUI</p>
                    <p className="font-mono text-slate-800">{duplicateModal.existing.cui}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-slate-500">Data assinatura</p>
                  <p className="font-medium text-slate-800">{duplicateModal.existing.data_assinatura || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Diferença</p>
                  <p className="font-medium text-slate-800">{duplicateModal.diff_days} dia(s) (janela: {duplicateModal.window_days})</p>
                </div>
              </div>
            </div>
          )}
          <div className="text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-lg p-3">
            💡 <strong>Como proceder:</strong> Se este é o mesmo cliente, solicite primeiro a <strong>anulação</strong> do contrato existente. Só depois pode inserir um novo.
          </div>
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setDuplicateModal(null)} disabled={saving} data-testid="duplicate-cancel">
              Cancelar
            </Button>
            {duplicateModal?.existing?.id && (
              <Button
                variant="outline"
                onClick={() => {
                  const ex = duplicateModal.existing;
                  window.open(`/contracts/${ex.service_category}/${ex.company}/${ex.id}`, '_blank', 'noopener,noreferrer');
                }}
                disabled={saving}
                data-testid="duplicate-view-existing"
              >
                Ver contrato em nova tab ↗
              </Button>
            )}
            {duplicateModal?.can_force && (
              <Button
                onClick={() => performSave({ force: true })}
                disabled={saving}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="duplicate-force"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Forçar gravação assim mesmo
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fluxo de confirmação de comprovativo (2 perguntas) — abre ao detetar NIF existente */}
      <Dialog open={Boolean(nifCheckStep)} onOpenChange={(v) => { if (!v) { setNifCheckStep(null); if (!formData.nif_comprovativo) { sendNifDivergenceAlert(); setNifBlocked(true); } } }}>
        <DialogContent className="sm:max-w-md" data-testid="nif-check-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5" />
              NIF já existente
            </DialogTitle>
            <DialogDescription>
              Este NIF já pertence a <strong>{nifNameWarning?.existing?.nome_cliente || 'outro cliente'}</strong>
              {nifNameWarning?.existing?.codigo ? ` (contrato #${nifNameWarning.existing.codigo})` : ''}.
              {' '}Um NIF pertence a uma só pessoa.
            </DialogDescription>
          </DialogHeader>

          {nifCheckStep === 'step1' && (
            <div className="space-y-4" data-testid="nif-check-step1">
              <p className="text-sm font-medium text-slate-800">Tem como comprovar este NIF?</p>
              <DialogFooter className="flex flex-row gap-2">
                <Button
                  variant="outline"
                  className="flex-1 border-rose-300 text-rose-700 hover:bg-rose-50"
                  onClick={() => { sendNifDivergenceAlert(); setNifBlocked(true); setNifCheckStep(null); }}
                  data-testid="nif-check-step1-nao"
                >
                  Não
                </Button>
                <Button
                  className="flex-1 bg-[#2D3E6F] hover:bg-[#1a2a4a] text-white"
                  onClick={() => setNifCheckStep('step2')}
                  data-testid="nif-check-step1-sim"
                >
                  Sim
                </Button>
              </DialogFooter>
            </div>
          )}

          {nifCheckStep === 'step2' && (
            <div className="space-y-4" data-testid="nif-check-step2">
              <p className="text-sm font-medium text-slate-800">
                Verificou com um documento fidedigno que o NIF está correto?
              </p>
              <DialogFooter className="flex flex-row gap-2">
                <Button
                  variant="outline"
                  className="flex-1 border-rose-300 text-rose-700 hover:bg-rose-50"
                  onClick={() => { sendNifDivergenceAlert(); setNifBlocked(true); setNifCheckStep(null); }}
                  data-testid="nif-check-step2-nao"
                >
                  Não
                </Button>
                <Button
                  className="flex-1 bg-[#2D3E6F] hover:bg-[#1a2a4a] text-white"
                  onClick={() => setNifCheckStep('step3')}
                  data-testid="nif-check-step2-sim"
                >
                  Sim
                </Button>
              </DialogFooter>
            </div>
          )}

          {nifCheckStep === 'step3' && (
            <div className="space-y-3" data-testid="nif-check-step3">
              <p className="text-sm font-medium text-slate-800">
                Carregue o comprovativo do NIF para poder continuar.
              </p>
              <p className="text-xs text-slate-500">
                O contrato será criado como <strong>DIVERGENTE</strong> e o Gestor/Super Admin serão notificados para verificação.
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  id="nif-proof-input"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) setNifProofFile(f); e.target.value = ''; }}
                  data-testid="nif-proof-input"
                />
                <Button type="button" variant="outline" className="gap-2" asChild>
                  <label htmlFor="nif-proof-input" className="cursor-pointer">
                    <Upload className="w-4 h-4" /> Escolher ficheiro
                  </label>
                </Button>
                {nifProofFile && (
                  <span className="text-xs text-emerald-700 flex items-center gap-1 truncate max-w-[220px]" data-testid="nif-proof-filename">
                    <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" /> {nifProofFile.name}
                  </span>
                )}
              </div>
              <DialogFooter className="flex flex-row gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 border-rose-300 text-rose-700 hover:bg-rose-50"
                  onClick={() => { sendNifDivergenceAlert(); setNifProofFile(null); setNifBlocked(true); setNifCheckStep(null); }}
                  data-testid="nif-check-step3-cancel"
                >
                  Não consigo comprovar
                </Button>
                <Button
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  disabled={!nifProofFile}
                  onClick={() => {
                    setFormData(prev => ({ ...prev, nif_comprovativo: true }));
                    setNifBlocked(false);
                    setNifCheckStep(null);
                    toast.success('Comprovativo anexado. Pode continuar — o contrato ficará marcado como DIVERGENTE.');
                  }}
                  data-testid="nif-check-step3-continuar"
                >
                  Continuar
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* NIF pertence a outra pessoa — bloqueio + confirmação de comprovativo */}
      <Dialog open={Boolean(nifConflictModal)} onOpenChange={(v) => { if (!v && !saving) setNifConflictModal(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="nif-conflict-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5" />
              NIF já registado noutro cliente
            </DialogTitle>
            <DialogDescription>
              {nifConflictModal?.message || 'Este NIF já pertence a outro cliente. Um NIF pertence a uma só pessoa.'}
            </DialogDescription>
          </DialogHeader>
          {nifConflictModal?.existing && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 space-y-2 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-xs text-slate-500">Contrato existente</p>
                  <p className="font-mono font-semibold text-rose-800">#{nifConflictModal.existing.codigo}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Cliente registado</p>
                  <p className="font-medium text-slate-800">{nifConflictModal.existing.nome_cliente || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">NIF</p>
                  <p className="font-mono text-slate-800">{nifConflictModal.nif || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Novo cliente</p>
                  <p className="font-medium text-slate-800">{nifConflictModal.nome_cliente || '—'}</p>
                </div>
              </div>
            </div>
          )}
          <label className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 cursor-pointer mt-2">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 accent-amber-600"
              checked={nifComprovativoChecked}
              onChange={(e) => setNifComprovativoChecked(e.target.checked)}
              data-testid="nif-comprovativo-checkbox"
            />
            <span className="text-sm text-amber-900">
              <strong>Tenho comprovativo do NIF.</strong> Confirmo que este NIF pertence ao cliente indicado.
              O contrato será criado e marcado como <strong>DIVERGENTE</strong>, e será enviado um alerta urgente ao Gestor/Super Admin.
            </span>
          </label>
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setNifConflictModal(null)} disabled={saving} data-testid="nif-conflict-cancel">
              Cancelar
            </Button>
            {nifConflictModal?.existing?.id && (
              <Button
                variant="outline"
                onClick={() => window.open(`/contracts/${nifConflictModal.existing.service_category || category}/${nifConflictModal.existing.company || company}/${nifConflictModal.existing.id}`, '_blank', 'noopener,noreferrer')}
                disabled={saving}
                data-testid="nif-conflict-view-existing"
              >
                Ver contrato existente ↗
              </Button>
            )}
            <Button
              onClick={() => performSave({ nifComprovativo: true })}
              disabled={saving || !nifComprovativoChecked}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="nif-conflict-confirm"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Avançar (marcar Divergente)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
